import {
    DEFAULT_RARITY,
    EPIC_RARITY,
    LEGENDARY_RARITY,
    RARE_RARITY
} from 'app/utils/Consts';
import LootInterface from 'app/interfaces/LootInterface';
import {fixed2int} from 'app/utils/fixed';
import toNumber from 'app/utils/toNumber';

const CHANCE_DECIMALS = 1;
const RARE_MIN_PRICE = 5;
const EPIC_MIN_PRICE = 20;
const LEGENDARY_MIN_PRICE = 50;

const isNullOrUndefined = (value?: number | string | null): value is undefined | null => typeof value === 'undefined' || value === null;

const roundChance = (value: number): number => Number(value.toFixed(CHANCE_DECIMALS));

const formatFixed = (value: number, suffix: string): string => `${value.toFixed(CHANCE_DECIMALS)}${suffix}`;

export const rarityLabel = (rarity: string): string => rarity.charAt(0).toUpperCase() + rarity.slice(1);

export const formatDropChance = (chance?: number | string | null): string | null => {
    if (isNullOrUndefined(chance) || Number.isNaN(toNumber(chance))) {
        return null;
    }

    return formatFixed(toNumber(chance), '%');
};

export const formatLootboxMoney = (value: number | string): string => `$${fixed2int(value)}`;

export const formatPrizePrice = (price?: number | string | null): string | null => {
    if (isNullOrUndefined(price) || toNumber(price) <= 0) {
        return null;
    }

    return formatLootboxMoney(price);
};

export const rarityFromPrice = (price?: number | string | null): string => {
    if (isNullOrUndefined(price) || Number.isNaN(toNumber(price))) {
        return DEFAULT_RARITY;
    }

    const value = toNumber(price);

    switch (true) {
    case value >= LEGENDARY_MIN_PRICE:
        return LEGENDARY_RARITY;
    case value >= EPIC_MIN_PRICE:
        return EPIC_RARITY;
    case value >= RARE_MIN_PRICE:
        return RARE_RARITY;
    default:
        return DEFAULT_RARITY;
    }
};

export const lootRarity = (loot: LootInterface): string => loot.rarity || rarityFromPrice(loot.calculated_price);

export interface RarityTotal {
    rarity: string
    chance: number
}

export const rarityTotals = (loot: LootInterface[]): RarityTotal[] => {
    const sums = loot.reduce((acc: Record<string, number>, item: LootInterface) => {
        const rarity = lootRarity(item);

        acc[rarity] = (acc[rarity] || 0) + toNumber(item.chance_percent || 0);
        return acc;
    }, {});

    return Object.keys(sums)
        .map((rarity: string) => ({chance: roundChance(sums[rarity]), rarity}))
        .filter((item: RarityTotal) => item.chance > 0)
        .sort((left: RarityTotal, right: RarityTotal) => left.chance - right.chance);
};
